// src/components/features/content/ContentList.jsx
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Schedule as ScheduleIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  BarChart as AnalyticsIcon,
  Article as ContentIcon
} from '@mui/icons-material';

// Import container component
import EntityListContainer from '../../../components/common/EntityListContainer';
import { ContentCard } from '../../../components/core/cards';
import { EntityStatusBadge } from '../../../components/core';
import ContentTypeIcon from '../../ui/ContentTypeIcon';
import useConfirmDialog from '../../../hooks/useConfirmationDialog';

// Import stores
import useContentStore from '../../../stores/contentStore';
import useBrandStore from '../../../stores/brandStore';
import useCampaignStore from '../../../stores/campaignStore';
import useNotificationStore from '../../../stores/notificationStore';
import useAuthStore from '../../../stores/authStore';
import useUIStore from '../../../stores/uiStore';

const ContentList = () => {
  const navigate = useNavigate();
  
  // Store hooks
  const { 
    content, meta, isLoading, error, 
    fetchContent, deleteContent, approveContent, publishContent
  } = useContentStore();

  const { contentListView, setContentView } = useUIStore();
  const { showSuccess, showError } = useNotificationStore();
  const { hasPermission } = useAuthStore();
  const { brands, fetchBrands } = useBrandStore();
  const { campaigns, fetchCampaigns } = useCampaignStore();
  
  // Confirmation dialog
  const [ConfirmDialog, confirm] = useConfirmDialog();
  
  // Check permissions
  const canCreate = hasPermission('content', 'create');
  const canUpdate = hasPermission('content', 'update');
  const canDelete = hasPermission('content', 'delete');
  const canApprove = hasPermission('approvals', 'update');
  
  // Load brands and campaigns for filters
  useEffect(() => {
    fetchBrands({ limit: 100 });
    fetchCampaigns({ limit: 100 });
  }, [fetchBrands, fetchCampaigns]);

  // Helper functions for brand and campaign names
  const getBrandName = useCallback((brandId) => {
    if (!brandId) return 'N/A';
    if (typeof brandId === 'object' && brandId !== null) return brandId.name;
    const brand = brands.find(b => b._id === brandId);
    return brand ? brand.name : 'N/A';
  }, [brands]);
  
  const getCampaignName = useCallback((campaignId) => {
    if (!campaignId) return 'N/A';
    if (typeof campaignId === 'object' && campaignId !== null) return campaignId.name;
    const campaign = campaigns.find(c => c._id === campaignId);
    return campaign ? campaign.name : 'N/A';
  }, [campaigns]);

  // Filter and sort configuration
  const filterConfig = [
    {
      name: 'status',
      label: 'Status',
      options: [
        { value: 'draft', label: 'Draft' },
        { value: 'pending', label: 'Pending Approval' },
        { value: 'approved', label: 'Approved' },
        { value: 'scheduled', label: 'Scheduled' },
        { value: 'published', label: 'Published' },
        { value: 'rejected', label: 'Rejected' }
      ],
      allLabel: 'All Statuses'
    },
    {
      name: 'type',
      label: 'Content Type',
      options: [
        { value: 'post', label: 'Post' },
        { value: 'story', label: 'Story' },
        { value: 'video', label: 'Video' },
        { value: 'reel', label: 'Reel' }
      ],
      allLabel: 'All Types'
    },
    {
      name: 'brandId',
      label: 'Brand',
      options: brands.map(brand => ({ value: brand._id, label: brand.name })),
      allLabel: 'All Brands'
    },
    {
      name: 'campaignId',
      label: 'Campaign',
      options: campaigns.map(campaign => ({ value: campaign._id, label: campaign.name })),
      allLabel: 'All Campaigns'
    },
    {
      name: 'category',
      label: 'Category',
      options: [
        { value: 'promotion', label: 'Promotion' },
        { value: 'announcement', label: 'Announcement' },
        { value: 'engagement', label: 'Engagement' },
        { value: 'educational', label: 'Educational' },
        { value: 'entertainment', label: 'Entertainment' }
      ],
      allLabel: 'All Categories'
    }
  ];
  
  const sortOptions = [
    { field: 'title', label: 'Title' },
    { field: 'createdAt', label: 'Created Date' },
    { field: 'updatedAt', label: 'Updated Date' },
    { field: 'publishDate', label: 'Publish Date' }
  ];

  // Handle content actions
  const handlePublishContent = async (contentItem) => {
    const confirmed = await confirm({
      title: 'Publish Content',
      content: `Are you sure you want to publish "${contentItem.title}" now?`,
      confirmText: 'Publish',
      cancelText: 'Cancel'
    });
    
    if (confirmed) {
      try {
        const publishingResponse = {
          success: true,
          postId: `temp-${Date.now()}`,
          postUrl: null
        };
        
        const platform = contentItem.distributions?.[0]?.platform || 'facebook';
        await publishContent(contentItem._id, platform, publishingResponse);
        showSuccess('Content published successfully');
      } catch (error) {
        showError(`Failed to publish content: ${error.message}`);
      }
    }
  };
  
  const handleApproveContent = async (contentItem) => {
    const confirmed = await confirm({
      title: 'Approve Content',
      content: `Are you sure you want to approve "${contentItem.title}"?`,
      confirmText: 'Approve',
      cancelText: 'Cancel'
    });
    
    if (confirmed) {
      try {
        await approveContent(contentItem._id);
        showSuccess('Content approved successfully');
      } catch (error) {
        showError(`Failed to approve content: ${error.message}`);
      }
    }
  };
  
  const handleDeleteContent = async (contentItem) => {
    const confirmed = await confirm({
      title: 'Delete Content',
      content: `Are you sure you want to delete "${contentItem.title}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDelete: true
    });
    
    if (confirmed) {
      try {
        await deleteContent(contentItem._id);
        showSuccess('Content deleted successfully');
      } catch (error) {
        showError(`Failed to delete content: ${error.message}`);
      }
    }
  };
  
  // Render content item for grid view
  const renderContentItem = useCallback((contentItem) => {
    return (
      <ContentCard
        content={contentItem}
        linkTo={`/content/${contentItem._id}`}
        onView={() => navigate(`/content/${contentItem._id}`)}
        onEdit={canUpdate ? () => navigate(`/content/${contentItem._id}/edit`) : undefined}
        onDelete={canDelete ? () => handleDeleteContent(contentItem) : undefined}
        onAnalytics={() => navigate(`/analytics/content/${contentItem._id}`)}
        chips={[
          { label: contentItem.type?.charAt(0)?.toUpperCase() + contentItem.type?.slice(1) || 'Post', color: 'default' },
          ...(contentItem.category ? [{ label: contentItem.category, color: 'primary' }] : [])
        ]}
        stats={[
          { label: 'Views', value: contentItem.metrics?.views || 0 },
          { label: 'Engagement', value: contentItem.metrics?.engagement || 0 }
        ]}
      />
    );
  }, [canUpdate, canDelete, navigate, handleDeleteContent]);

  // Table columns configuration
  const tableColumns = [
    { 
      id: 'title', 
      label: 'Title',
      renderCell: (item) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ContentTypeIcon type={item.type} showTooltip />
          <Typography variant="body1">{item.title}</Typography>
        </Box>
      )
    },
    { 
      id: 'status', 
      label: 'Status',
      align: 'center',
      renderCell: (item) => (
        <EntityStatusBadge status={item.status} entityType="content" />
      )
    },
    { 
      id: 'brand', 
      label: 'Brand',
      align: 'center',
      renderCell: (item) => getBrandName(item.brandId)
    },
    { 
      id: 'campaign', 
      label: 'Campaign',
      align: 'center',
      renderCell: (item) => getCampaignName(item.campaignId)
    },
    { 
      id: 'publishDate', 
      label: 'Publish Date',
      align: 'center',
      renderCell: (item) => item.publishDate ? new Date(item.publishDate).toLocaleDateString() : 'Not scheduled'
    }
  ];

  // Table row actions
  const getRowActions = useCallback((item) => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        {canUpdate && (
          <Tooltip title="Edit">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/content/${item._id}/edit`);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {item.status === 'approved' && canUpdate && (
          <Tooltip title="Schedule">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/content/${item._id}/schedule`);
              }}
            >
              <ScheduleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {item.status === 'scheduled' && canUpdate && (
          <Tooltip title="Publish Now">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                handlePublishContent(item);
              }}
            >
              <PublishIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {item.status === 'pending' && canApprove && (
          <Tooltip title="Approve">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                handleApproveContent(item);
              }}
            >
              <ApproveIcon fontSize="small" color="success" />
            </IconButton>
          </Tooltip>
        )}
        
        {item.status === 'pending' && canApprove && (
          <Tooltip title="Reject">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/content/${item._id}/reject`);
              }}
            >
              <RejectIcon fontSize="small" color="error" />
            </IconButton>
          </Tooltip>
        )}
        
        {item.status === 'published' && (
          <Tooltip title="Analytics">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/content/${item._id}/analytics`);
              }}
            >
              <AnalyticsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        {canDelete && (
          <Tooltip title="Delete">
            <IconButton 
              size="small" 
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteContent(item);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }, [canUpdate, canApprove, canDelete, navigate, handlePublishContent, handleApproveContent, handleDeleteContent]);

  return (
    <>
      <EntityListContainer
        title="Content"
        description="Manage your content across all platforms"
        fetchData={fetchContent}
        renderEntity={renderContentItem}
        
        initialFilters={{
          status: 'all',
          type: 'all',
          brandId: 'all',
          campaignId: 'all',
          category: 'all'
        }}
        
        initialSort={{ field: 'updatedAt', direction: 'desc' }}
        filterConfig={filterConfig}
        sortOptions={sortOptions}
        
        // View options
        availableViews={['grid', 'table']}
        defaultView={contentListView}
        
        // Table configuration
        tableConfig={{
          columns: tableColumns,
          onRowClick: (item) => navigate(`/content/${item._id}`),
          renderRowActions: getRowActions
        }}
        
        // Use store values
        entities={content}
        isLoading={isLoading}
        error={error}
        metadata={meta}
        
        // Actions configuration
        actions={{
          canCreate,
          createPath: '/content/create',
          createLabel: 'Create Content'
        }}
        
        // Empty state configuration
        emptyState={{
          title: 'No Content Found',
          description: 'Get started by creating your first content piece.',
          icon: <ContentIcon sx={{ fontSize: 64 }} />,
          getFilteredTitle: () => 'No Matching Content',
          getFilteredDescription: () => 'Try adjusting your search or filters to find what you\'re looking for.'
        }}
      />
      
      <ConfirmDialog />
    </>
  );
};

export default ContentList;